// Plan and billing (BILLING-001 summary + BILLING-09 entitlements, IA §2.1 /{org}/settings/billing):
// the current plan and one usage meter per counted quota. Every vendor is on the free plan (D-015).
// Phase 7a (P7-A): the trial (truthful state, Owner action), credit (balance / held / available,
// link to the statement), add-ons, invoices and the refund policy. Checkout, top-ups and add-on
// purchase need the owner's gateway account and show "pending activation", never a live button.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { Badge, BusyRegion, SkeletonText, UsageMeter, useToast } from "@ady/ui";
import { errorMessage } from "../api/errors";
import { keys, useBilling, useEntitlements } from "../api/queries";
import type { QuotaUsage } from "../api/types";
import {
  useAddons,
  useCredits,
  useInvoices,
  useWallet,
  walletApi,
  type BillingSummary7,
} from "../billing/api";
import {
  AddonsPanel,
  CreditSummary,
  InvoicesTable,
  RefundPolicy,
  TrialPanel,
} from "../billing/components";
import { InvoicePopup } from "../billing/ReferShop";
import { AppLink } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import { TID } from "../testids";
import { LoadError, SettingsPage, SettingsSection } from "./SettingsPage";

/** Quotas that are limits, not counters: shown as a value, never as a meter. */
const LIMIT_ONLY = new Set(["audit_retention_days", "api_rate_per_min"]);
const MONTHLY = new Set(["orders_month", "ai_credits_month", "sms_month"]);
/** Display order; unknown keys follow alphabetically. */
const ORDER = [
  "staff",
  "stores",
  "products",
  "orders_month",
  "locations",
  "storage_bytes",
  "theme_versions",
  "pos_registers",
  "api_keys",
  "webhook_endpoints",
  "automations",
  "ai_credits_month",
  "sms_month",
  "test_mode_stores",
];

const intl = new Intl.NumberFormat("en-IN");
const n = (v: number) => intl.format(v);

export function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 || Number.isInteger(v) ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export const quotaLabel = (key: string) =>
  i18n.tDynamic(`billing.quotas.${key}`, "billing.quotas.unknown");

function valueText(key: string, q: QuotaUsage): string {
  if (q.limit === null) return t("billing.unlimited");
  if (q.limit === 0) return t("billing.notIncluded");
  const fmt = key === "storage_bytes" ? formatBytes : n;
  const params = { used: fmt(q.used), limit: fmt(q.limit) };
  return MONTHLY.has(key) ? t("billing.usedOfMonth", params) : t("billing.usedOf", params);
}

function limitText(key: string, q: QuotaUsage): string {
  if (q.limit === null) return t("billing.unlimited");
  if (key === "audit_retention_days") return t("billing.days", { limit: n(q.limit) });
  if (key === "api_rate_per_min") return t("billing.perMinute", { limit: n(q.limit) });
  return n(q.limit);
}

export function BillingPage() {
  const { membership, storeSlug } = useShell();
  const orgId = membership.org.id;
  const canRead = can(membership, ["billing.read"]);
  const summary = useBilling(orgId, canRead);
  const entitlements = useEntitlements(orgId, canRead);
  const quotas = Object.entries(entitlements.data?.quotas ?? {});
  const rank = (k: string) => {
    const i = ORDER.indexOf(k);
    return i < 0 ? ORDER.length : i;
  };
  const counted = quotas
    .filter(([k]) => !LIMIT_ONLY.has(k))
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
  const limits = quotas.filter(([k]) => LIMIT_ONLY.has(k));
  const data = summary.data as BillingSummary7 | undefined;
  const plan = data?.plan;
  const sub = data?.subscription;
  const wallet = useWallet(orgId, canRead);
  const addons = useAddons(orgId, canRead);
  const invoices = useInvoices(orgId, canRead);
  const [invoice, setInvoice] = useState<string | null>(null);
  const credits = useCredits(orgId, canRead);
  const qc = useQueryClient();
  const toast = useToast();
  const [trialError, setTrialError] = useState<string>();
  const startTrial = useMutation({
    mutationFn: () => walletApi.startTrial(orgId, data?.trial?.plans[0]?.id),
    onSuccess: async () => {
      setTrialError(undefined);
      toast.show({ tone: "success", title: t("wallet.trial.started") });
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.billing(orgId) }),
        qc.invalidateQueries({ queryKey: keys.entitlements(orgId) }),
      ]);
    },
    onError: (err) => setTrialError(errorMessage(err)),
  });

  return (
    <SettingsPage
      title={t("billing.title")}
      lead={t("billing.lead", { org: membership.org.name })}
      anyOf={["billing.read"]}
      testId={TID.billingPage}
      canonical="BILLING-001"
    >
      <div className="grid items-start gap-3 lg:grid-cols-2">
        <SettingsSection title={t("billing.planTitle")} id="billing-plan">
          {summary.isError ? (
            <LoadError error={summary.error} onRetry={() => void summary.refetch()} />
          ) : (
            <BusyRegion busy={summary.isPending} skeleton={<SkeletonText lines={2} />}>
              {plan ? (
                <div className="flex flex-col gap-2">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="text-title-sm font-semibold">
                      {i18n.has(`plans.${plan.key}`)
                        ? i18n.tDynamic(`plans.${plan.key}`, "plans.free")
                        : plan.name}
                    </span>
                    {sub ? (
                      <Badge tone={sub.state === "active" ? "success" : "warning"}>
                        {i18n.tDynamic(
                          `billing.planStates.${sub.state}`,
                          "billing.planStates.unknown",
                        )}
                      </Badge>
                    ) : null}
                  </p>
                  {sub ? (
                    <p className="text-sm text-fg-secondary">
                      {t("billing.since", { when: formatDate(sub.starts_at, { style: "medium" }) })}
                    </p>
                  ) : null}
                  {sub?.kind === "paid" && sub.state === "grace" && sub.grace_ends_at ? (
                    <p className="text-sm">
                      {t("wallet.graceEnds", {
                        when: formatDate(sub.grace_ends_at, { style: "medium" }),
                      })}
                    </p>
                  ) : sub?.kind === "paid" && sub.ends_at ? (
                    <p className="text-sm text-fg-secondary">
                      {t("wallet.periodEnds", {
                        when: formatDate(sub.ends_at, { style: "medium" }),
                      })}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-md text-fg-secondary">{t("billing.noPlan")}</p>
              )}
              <p className="pt-3 text-md">{t("billing.freeNote")}</p>
            </BusyRegion>
          )}
        </SettingsSection>

        <SettingsSection title={t("wallet.credit.title")} id="billing-credit">
          <p className="text-sm text-fg-secondary">{t("wallet.credit.lead")}</p>
          {wallet.isError ? (
            <LoadError error={wallet.error} onRetry={() => void wallet.refetch()} />
          ) : (
            <BusyRegion busy={wallet.isPending} skeleton={<SkeletonText lines={2} />}>
              {wallet.data ? (
                <CreditSummary
                  view={wallet.data}
                  statementLink={
                    <AppLink href={`/${membership.org.slug}/${storeSlug}/wallet`}>
                      {t("wallet.credit.viewStatement")}
                    </AppLink>
                  }
                />
              ) : null}
            </BusyRegion>
          )}
        </SettingsSection>
      </div>

      {data?.trial ? (
        <SettingsSection title={t("wallet.trial.title")} id="billing-trial">
          <TrialPanel
            trial={data.trial}
            isOwner={membership.role.key === "owner"}
            busy={startTrial.isPending}
            onStart={() => startTrial.mutate()}
          />
          {trialError ? (
            <p className="text-sm text-danger-fg" role="alert">
              {trialError}
            </p>
          ) : null}
        </SettingsSection>
      ) : null}

      <div className="grid items-start gap-3 lg:grid-cols-2">
        <SettingsSection title={t("billing.usageTitle")} id="billing-usage">
          <p className="text-sm text-fg-secondary">{t("billing.usageLead")}</p>
          {entitlements.isError ? (
            <LoadError error={entitlements.error} onRetry={() => void entitlements.refetch()} />
          ) : (
            <BusyRegion
              busy={entitlements.isPending}
              skeleton={<SkeletonText lines={6} />}
              data-testid={entitlements.isPending ? TID.loadingState : undefined}
            >
              <ul className="grid gap-x-8 gap-y-5 md:grid-cols-2">
                {counted.map(([key, q]) => (
                  <li key={key}>
                    <UsageMeter
                      label={quotaLabel(key)}
                      used={q.used}
                      limit={q.limit}
                      valueText={valueText(key, q)}
                      // A quota that is not included (limit 0) is not "at the limit".
                      state={q.limit === 0 ? "ok" : q.state}
                      data-testid={TID.usageMeter}
                      data-quota={key}
                    />
                  </li>
                ))}
              </ul>
            </BusyRegion>
          )}
        </SettingsSection>

        {limits.length ? (
          <SettingsSection title={t("billing.limitsTitle")} id="billing-limits">
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {limits.map(([key, q]) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <dt className="text-sm text-fg-secondary">{quotaLabel(key)}</dt>
                  <dd className="ady-ledger text-md font-medium">{limitText(key, q)}</dd>
                </div>
              ))}
            </dl>
          </SettingsSection>
        ) : null}
      </div>

      <SettingsSection title={t("wallet.addons.title")} id="billing-addons">
        <p className="text-sm text-fg-secondary">{t("wallet.addons.lead")}</p>
        {addons.isError ? (
          <LoadError error={addons.error} onRetry={() => void addons.refetch()} />
        ) : (
          <BusyRegion busy={addons.isPending} skeleton={<SkeletonText lines={3} />}>
            {addons.data ? <AddonsPanel view={addons.data} quotaName={quotaLabel} /> : null}
          </BusyRegion>
        )}
      </SettingsSection>

      <div className="grid items-start gap-3 lg:grid-cols-2">
        <SettingsSection title={t("wallet.invoices.title")} id="billing-invoices">
          <InvoicesTable
            invoices={invoices.data?.data ?? []}
            state={invoices.isError ? "error" : invoices.isPending ? "loading" : "ready"}
            onRetry={() => void invoices.refetch()}
            onView={setInvoice}
          />
          <InvoicePopup orgId={orgId} invoiceId={invoice} onClose={() => setInvoice(null)} />
        </SettingsSection>

        <SettingsSection title={t("wallet.refunds.title")} id="billing-refunds">
          {credits.data ? <RefundPolicy policy={credits.data.refund_policy} /> : null}
          {credits.isError ? (
            <LoadError error={credits.error} onRetry={() => void credits.refetch()} />
          ) : null}
          <p className="text-sm text-fg-secondary">{t("wallet.checkout.body")}</p>
        </SettingsSection>
      </div>
    </SettingsPage>
  );
}
